import { useRef, useState } from "react";
import { HallSummary, ManagementApi, ManagementApiError } from "./api";
import { Locale } from "./copy";
import { offsetMinutes, utcOffsets } from "./companyTime";

export const hallLabels = {
  en: {
    title: "Onboard a customer company", help: "Platform administrators only. Creates a provisioning record, not a live game. The server verifies your permissions and records the staff audit.",
    id: "Company identifier", slug: "Company short name", name: "Company display name", language: "Default language",
    zone: "UTC offset", close: "Operational day closes at (hall local time)",
    submit: "Create company", busy: "Creating company…", saved: "Company created in provisioning state.", another: "Create another company",
    invalid: "Check the company short name, display name and UTC offset.",
    denied: "Your staff role cannot create halls. A platform administrator is required.",
    expired: "Your sign-in has expired or is unavailable. Sign in again before retrying.",
    conflict: "This company short name is already reserved. Check active and removed companies.",
    failed: "Creation was not confirmed. Retry unchanged fields to safely recover the same request.",
  },
  es: {
    title: "Registrar una empresa cliente", help: "Solo administradores de plataforma. Crea un registro en preparación, no un juego en vivo. El servidor verifica sus permisos y registra la auditoría del personal.",
    id: "Identificador de empresa", slug: "Nombre corto de empresa", name: "Nombre visible de empresa", language: "Idioma predeterminado",
    zone: "Diferencia UTC", close: "Cierre del día operativo (hora local de la sala)",
    submit: "Crear empresa", busy: "Creando empresa…", saved: "Empresa creada en estado de preparación.", another: "Crear otra empresa",
    invalid: "Revise el identificador, nombre corto en minúsculas, nombre visible, zona horaria y hora de cierre local.",
    denied: "Su rol no permite crear salas. Se requiere un administrador de plataforma.",
    expired: "Su sesión ha vencido o no está disponible. Inicie sesión antes de reintentar.",
    conflict: "El identificador o nombre corto ya existe. Revise la lista de salas antes de cambiarlo.",
    failed: "No se confirmó la creación. Reintente sin cambiar los campos para recuperar la misma solicitud de forma segura.",
  },
} as const;

export interface HallDraft {
  slug: string; display_name: string; time_zone: string;
}
const emptyDraft = (): HallDraft => ({ slug: "", display_name: "", time_zone: "UTC-04:00" });

export function hallPayload(draft: HallDraft): HallDraft & { status: "provisioning"; default_language: "en"; operational_day_close_local: "00:00" } {
  // Select the exact wire fields: never forward arbitrary form properties or a live status.
  const value = { slug: draft.slug.trim().toLowerCase(), display_name: draft.display_name.trim(),
    default_language: "en" as const, time_zone: draft.time_zone, operational_day_close_local: "00:00" as const, status: "provisioning" as const };
  if (!/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(value.slug)
    || !value.display_name || value.display_name.length > 120
    || !utcOffsets.includes(value.time_zone)) throw new Error("invalid_hall");
  try { if (offsetMinutes(value.time_zone) === undefined) throw new Error(); }
  catch { throw new Error("invalid_hall"); }
  return value;
}

// Component-owned identity survives language changes and uncertain retries, independently
// of other management mutations. No credential or request body is stored in browser storage.
export class HallCreation {
  private pending?: { fingerprint: string; key: string };
  async submit(api: ManagementApi, draft: HallDraft): Promise<HallSummary> {
    const payload = hallPayload(draft);
    const fingerprint = JSON.stringify(payload);
    if (this.pending?.fingerprint !== fingerprint) this.pending = { fingerprint, key: crypto.randomUUID() };
    return api.mutate<HallSummary>("/v1/halls", "POST", payload, this.pending.key);
  }
}

export function HallOnboarding({ api, locale, onCreated }: { api: ManagementApi; locale: Locale; onCreated: (hall: HallSummary) => void }): React.JSX.Element {
  const text = hallLabels[locale];
  const [draft, setDraft] = useState<HallDraft>(emptyDraft);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<HallSummary>();
  const [error, setError] = useState<"invalid" | "denied" | "expired" | "conflict" | "failed">();
  const creation = useRef(new HallCreation());
  const submitting = useRef(false);
  const change = (field: keyof HallDraft, value: string): void => { setDraft(previous => ({ ...previous, [field]: value })); setError(undefined); };
  const submit = async (): Promise<void> => {
    if (submitting.current || created) return;
    submitting.current = true; setBusy(true); setError(undefined);
    try {
      try { hallPayload(draft); } catch { setError("invalid"); return; }
      const hall = await creation.current.submit(api, draft);
      setCreated(hall); onCreated(hall);
    } catch (failure: unknown) {
      setError(failure instanceof ManagementApiError
        ? failure.status === 403 ? "denied" : failure.status === 401 ? "expired" : failure.status === 409 ? "conflict" : failure.status === 422 ? "invalid" : "failed"
        : "failed");
    } finally { submitting.current = false; setBusy(false); }
  };
  return <section className="admin-panel" aria-labelledby="hall-create-heading">
    <h2 id="hall-create-heading">{text.title}</h2><p className="muted">{text.help}</p>
    {error && <p className="inline-notice" role="alert">{text[error]}</p>}
    {created ? <><p role="status">{text.saved} <code>{created.hall_id}</code></p><button onClick={() => { setCreated(undefined); setDraft(emptyDraft()); creation.current = new HallCreation(); }}>{text.another}</button></>
      : <form onSubmit={event => { event.preventDefault(); void submit(); }}>
        <fieldset disabled={busy}><div className="form-grid">
          <label>{text.slug}<input required maxLength={63} value={draft.slug} onChange={event => change("slug", event.target.value)} /></label>
          <p className="muted">{draft.slug || (locale === "es" ? "nombre-de-sala" : "hall-name")}.fivecrownsystem.com — {locale === "es" ? "La dirección se configura automáticamente. Los nombres del sistema están reservados." : "The company address is configured automatically. System names are reserved."}</p>
          <label>{text.name}<input required maxLength={120} value={draft.display_name} onChange={event => change("display_name", event.target.value)} /></label>
          <label>{text.zone}<select required value={draft.time_zone} onChange={event => change("time_zone", event.target.value)}>{utcOffsets.map(zone => <option key={zone} value={zone}>{zone.replace("-", "−")}</option>)}</select></label>
        </div><button type="submit">{busy ? text.busy : text.submit}</button></fieldset>
      </form>}
  </section>;
}
